import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";

import { API_URL } from "../../api";
import { tokens } from "../../theme";
import Header from "../../components/Header1";

const PromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [pageSize] = useState(50);
  const [pageNo, setPageNo] = useState(0);
  const [total, setTotal] = useState(0);

  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Fetch data function
  const fetchPromoCodes = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}admin/promotioncode/?limit=${pageSize}&pageNo=${pageNo + 1}`
      );
      const data = await response.json();
      const formattedData = data.results.map(item => ({
        ...item,
        created_at: new Date(item.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
      }));
      setPromoCodes(formattedData);
      setTotal(data.total);
    } catch (error) {
      console.error("Failed to fetch promo codes:", error);
    }
  }, [pageSize, pageNo]);

  useEffect(() => {
    fetchPromoCodes();
  }, [fetchPromoCodes]);

  // Columns for DataGrid
  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", flex: 0.5 },
      { field: "promo_id", headerName: "Promo ID", flex: 1 },
      {
        field: "percent_off",
        headerName: "Percent Off",
        type: "number",
        flex: 1,
      },
      {
        field: "amount_off",
        headerName: "Amount Off",
        type: "number",
        flex: 1,
      },
      {
        field: "max_redemptions",
        headerName: "Max Redemptions",
        type: "number",
        flex: 1,
      },
      { field: "applies_to", headerName: "Applies To", flex: 1 },
      { field: "usages", headerName: "Usage", flex: 1 },
      {
        field: "once_per_user",
        headerName: "Once Per User",
        type: "boolean",
        flex: 1,
      },
      {
        field: "created_at",
        headerName: "Created At",
        type: "string",
        flex: 1,
      },
      {
        field: "status",
        headerName: "Link",
        flex: 1,
        renderCell: params => (
          <Link
            to={`/promo-codes/${params.id}`}
            style={{ color: colors.blueAccent[400], textDecoration: "none" }}
          >
            Show
          </Link>
        ),
      },
    ],
    [colors.blueAccent]
  );

  return (
    <Box m="20px">
      <Header title="Promotional Codes" subtitle="List of Promotional Codes" />
      <Box display="flex" justifyContent="flex-end" mt={-7}>
        <Button
          sx={{
            mb: 2,
            background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
            color: "#fff",
            "&:hover": {
              background: "linear-gradient(90deg, #FC8C66, #F76A7B)",
            },
          }}
          onClick={() => navigate("/promo-codes/add")}
        >
          Add New Code
        </Button>
      </Box>

      <Box
        mt={4}
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#605f61",
            color: "#fff",
            borderBottom: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            fontWeight: "500",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#605f61",
            color: "#fff",
            borderTop: "none",
          },
          "& .MuiTablePagination-root": {
            color: "rgba(255, 255, 255, 0.87)",
            fontSize: "14px",
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={promoCodes}
          columns={columns}
          pagination
          paginationMode="server"
          pageSize={pageSize}
          rowsPerPageOptions={[50]}
          rowCount={total}
          onPageChange={newPage => setPageNo(newPage)}
          components={{ Toolbar: GridToolbar }}
          getRowId={row => row.id}
        />
      </Box>
    </Box>
  );
};

export default PromoCodes;
